import React, { useCallback, useMemo, useState } from 'react';
// import { useSignals } from '@preact/signals-react/runtime';
import { Marker, Rectangle, Polygon, Popup, Pane, useMapEvents } from 'react-leaflet'
import { divIcon, icon } from 'leaflet';
import { useWindowSize } from 'react-use';
import jsonData from '../../signals/jsonData';
import appState from '../../signals/appState';
import markerFav from './assets/marker-fav.svg';
import markerSearch from './assets/marker-search.svg';
import useRecord from '../../hooks/useRecord';
import './leaflet.css';

const FeatureWithText = ({ map, shape, positions, bounds, exhibitor, colors, isPrint }) => {
	// useSignals();

	const { currentLanguage, exhibitorInfo } = jsonData.value;
	const { selectedBooth } = appState.value;
	const [rectCurrent, setRectCurrent] = useState(null);

	const onRectLoad = useCallback((map) => {
		setRectCurrent(map);
	}, []);

	const { makeRecord } = useRecord();
	const recordExhibitorClicked = useCallback((booth) => {
		makeRecord({
			Action: 'T',
			Booth: booth,
		});
	}, [makeRecord]);

	const { width } = useWindowSize();

	const minZoom = useMemo(() => {
		if (width <= 1440) return 1;
		return 2;
	}, [width]);

	const [currentZoom, setCurrentZoom] = useState(minZoom);

	const mapEvents = useMapEvents({
		zoomend: () => {
			setCurrentZoom(mapEvents.getZoom());
		},
	});

	const exhibitorName = useMemo(() => {
		return exhibitor?.['廠商簡稱'] || '';
	}, [exhibitor]);

	const featureWidthPixels = useMemo(() => {
		if (map) {
			const pixelPosition = map.project([bounds[0][0], bounds[0][1]], currentZoom);
			const pixelPosition2 = map.project([bounds[1][0], bounds[1][1]], currentZoom);
			return `${(pixelPosition2.x - pixelPosition.x).toString().replace('-', '')}px`;
		}
		return '0';
	}, [bounds, currentZoom, map]);

	const featureHeightPixels = useMemo(() => {
		if (map) {
			const pixelPosition = map.project([bounds[0][0], bounds[0][1]], currentZoom);
			const pixelPosition2 = map.project([bounds[1][0], bounds[1][1]], currentZoom);
			return `${(pixelPosition2.y - pixelPosition.y).toString().replace('-', '')}px`;
		}
		return '0';
	}, [bounds, currentZoom, map]);

	const getTextBits = useCallback(() => {
		let charactersPerLine = 8;

		const textBits = [];
		const exhibitorNameLines = exhibitorName.split(' ');
		let currentLine = '';
		exhibitorNameLines.forEach((word) => {
			if (currentLine.length + word.length > charactersPerLine) {
				textBits.push(currentLine);
				currentLine = '';
			}
			currentLine += ` ${word}`;
		});
		textBits.push(currentLine);

		textBits.forEach((text, index) => {
			textBits[index] = text.trim();
		});

		return textBits;
	}, [exhibitorName]);

	const textIcon = useMemo(() => {
		const width = featureWidthPixels?.replace('px', '');
		const height = featureHeightPixels?.replace('px', '');
		const numWidth = Number(width);
		const numHeight = Number(height);
		const paddingTopBot = currentZoom * 1;
		const paddingLeftRight = currentZoom * 2;
		const canvas = document.createElement('canvas');

		canvas.width = numWidth;
		canvas.height = numHeight;

		const ctx = canvas.getContext('2d');

		let boothFontSize = currentZoom * 8;
		if (numHeight / boothFontSize < 2.5) boothFontSize = numHeight / 2.5;
		if (numWidth / boothFontSize < 2.5) boothFontSize = numWidth / 3;
		ctx.globalAlpha = (100 - Number(colors.textOpacity)) / 100;
		ctx.font = `${boothFontSize}px Noto Sans TC`;
		ctx.fillText(exhibitor?.['攤位號碼'], paddingLeftRight, boothFontSize + paddingTopBot, numWidth - paddingLeftRight * 2);

		if (currentLanguage.code.includes('zh')) {
			const avaialbleHeight = numHeight - (boothFontSize + paddingTopBot * 2);
			const avaialbleWidth = numWidth - paddingLeftRight * 2;
			const characterCount = exhibitorName.length;
			const characterWidth = avaialbleWidth / characterCount;

			let nameFontSize = boothFontSize;

			if (characterWidth < avaialbleHeight) nameFontSize = characterWidth;

			ctx.font = `${nameFontSize}px Noto Sans TC`;

			ctx.fillText(exhibitorName, paddingLeftRight, (boothFontSize + paddingTopBot) + (nameFontSize), numWidth - paddingLeftRight * 2);

		} else {

			let nameFontSize = currentZoom * 8;
			if (numHeight / nameFontSize < 2.5) nameFontSize = numHeight / 2.5;

			ctx.font = `${nameFontSize}px Noto Sans TC`;

			const textBits = getTextBits();

			textBits.forEach((text, index) => {
				ctx.fillText(text, paddingLeftRight, (boothFontSize + paddingTopBot) + (nameFontSize * (index + 1)), numWidth - paddingLeftRight * 2);
			});
		}

		const dataUrl = canvas.toDataURL();
		const html = `<img src='${dataUrl}' />`;

		return divIcon({
			html,
			className: 'booth-text',
			iconSize: [Number(width), Number(height)],
		});
	}, [colors.textOpacity, currentLanguage.code, currentZoom, exhibitor, exhibitorName, featureHeightPixels, featureWidthPixels, getTextBits]);

	const Feature = useMemo(() => {
		if (shape === 'rectangle') return Rectangle;
		if (shape === 'polygon') return Polygon;
	}, [shape]);

	const isSelected = useMemo(() => {
		return selectedBooth === exhibitor?.['攤位號碼'];
	}, [exhibitor, selectedBooth]);

	const isFav = useMemo(() => {
		if (isSelected) {
			const exhibitorInfoItem = exhibitorInfo.find(item => item['攤位號碼'] === exhibitor?.['攤位號碼']);
			if (exhibitorInfoItem) {
				return exhibitorInfoItem.favourite;
			}
		}
		return false;
	}, [exhibitor, exhibitorInfo, isSelected]);

	const center = useMemo(() => {
		if (rectCurrent) {
			return rectCurrent.getCenter();
		}
		return [0, 0];
	}, [rectCurrent]);

	const markerIcon = useMemo(() => {
		const size = currentZoom * 20;
		return icon({
			iconUrl: isFav ? markerFav : markerSearch,
			iconSize: [size, size],
		});
	}, [currentZoom, isFav]);

	const markerCenter = useMemo(() => {
		const offset = currentZoom * 5;
		if (map) {
			const pixelCenter = map.project(center, currentZoom);
			pixelCenter.y -= offset;

			const latLngCenter = map.unproject(pixelCenter, currentZoom);
			return latLngCenter;
		}

		return [0, 0];
	}, [center, currentZoom, map]);

	const chooseExhibitor = useCallback(() => {
		if (exhibitor) {
			appState.value = {
				...appState.value,
				selectedExhibitor: exhibitor['攤位號碼'],
				ExhibitorInfo: true,
			};

			recordExhibitorClicked(exhibitor['攤位號碼']);
		}
	}, [exhibitor, recordExhibitorClicked]);

	const isTouchDevice = useMemo(() => {
		return 'ontouchstart' in window || navigator.maxTouchPoints;
	}, []);

	const navigateToBooth = useCallback(() => {
		makeRecord({
			Action: 'N',
			Booth: exhibitor['攤位號碼'],
		});

		appState.value = {
			...appState.value,
			selectedBooth: exhibitor['攤位號碼'],
			navigation: true,
			ExhibitorInfo: true,
		};
	}, [exhibitor, makeRecord]);

	return (
		<Feature
			ref={onRectLoad}
			bounds={bounds}
			positions={positions}
			pathOptions={{
				color: colors.border,
				fillColor: colors.fill,
				fillOpacity: (100 - Number(colors.fillOpacity)) / 100,
				opacity: (100 - Number(colors.borderOpacity)) / 100,
				weight: 3,
			}}
		>
			{exhibitor ? (
				<Pane name={`marker${exhibitor?.['攤位號碼']}`} style={{ zIndex: 400 }}>
					<Marker position={center} icon={textIcon} eventHandlers={isPrint ? {} : {
						mouseover: () => {
							if (isTouchDevice) return;
							rectCurrent.fire('click');
						},
						click: () => {
							if (!isTouchDevice) return;
							rectCurrent.fire('click');
						},
					}} />
				</Pane>
			) : null}

			{isSelected ? (
				<Pane name='marker' style={{ zIndex: 400 }}>
					<Marker position={markerCenter} icon={markerIcon}>
					</Marker>
				</Pane>
			) : null}

			{exhibitor ? (
				<Popup>
					<div className='booth-info-popup' onClick={() => { chooseExhibitor(exhibitor) }}>
						<div className='title'>{exhibitorName}</div>
						<div className='info-wrap'>
							<div className='details'>
								{exhibitor?.['展商基本介紹'] || ''}
							</div>
						</div>
					</div>

					<div className='navigation-control' onClick={navigateToBooth}>
						<div className='icon marker' >
						</div>
					</div>
				</Popup>
			) : null}
		</Feature>
	)
};

export default FeatureWithText;